import React, { useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
  title?: string;
  description?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  autoFocus?: boolean;
  scrollToView?: boolean;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({
    className,
    variant = 'default',
    title,
    description,
    dismissible = false,
    onDismiss,
    autoFocus = false,
    scrollToView = false,
    children,
    ...props
  }, ref) => {
    const alertRef = useRef<HTMLDivElement>(null);
    const combinedRef = (ref as React.RefObject<HTMLDivElement>) || alertRef;

    useEffect(() => {
      if (scrollToView && combinedRef.current) {
        // Smooth scroll to the error message
        combinedRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }

      if (autoFocus && combinedRef.current) {
        // Focus the alert for screen readers
        combinedRef.current.focus();
      }
    }, [scrollToView, autoFocus]);

    const getIcon = () => {
      switch (variant) {
        case 'destructive':
          return <AlertCircle className="h-4 w-4" />;
        case 'success':
          return <CheckCircle className="h-4 w-4" />;
        case 'warning':
          return <AlertTriangle className="h-4 w-4" />;
        case 'info':
          return <Info className="h-4 w-4" />;
        default:
          return <Info className="h-4 w-4" />;
      }
    };

    const getVariantStyles = () => {
      switch (variant) {
        case 'destructive':
          return 'border-red-200 bg-red-50 text-red-900';
        case 'success':
          return 'border-green-200 bg-green-50 text-green-900';
        case 'warning':
          return 'border-yellow-200 bg-yellow-50 text-yellow-900';
        case 'info':
          return 'border-blue-200 bg-blue-50 text-blue-900';
        default:
          return 'border-gray-200 bg-white text-gray-900';
      }
    };

    const getIconColor = () => {
      switch (variant) {
        case 'destructive':
          return 'text-red-600';
        case 'success':
          return 'text-green-600';
        case 'warning':
          return 'text-yellow-600';
        case 'info':
          return 'text-blue-600';
        default:
          return 'text-gray-600';
      }
    };

    return (
      <div
        ref={combinedRef}
        role="alert"
        tabIndex={autoFocus ? 0 : -1}
        className={cn(
          'relative w-full border p-4 transition-all duration-200',
          getVariantStyles(),
          className
        )}
        {...props}
      >
        <div className="flex items-start space-x-3">
          <div className={cn('flex-shrink-0 mt-0.5', getIconColor())}>
            {getIcon()}
          </div>

          <div className="flex-1 min-w-0">
            {title && (
              <h3 className="text-sm font-medium mb-1">
                {title}
              </h3>
            )}

            {description && (
              <div className="text-sm opacity-90">
                {description}
              </div>
            )}

            {children && (
              <div className="text-sm">
                {children}
              </div>
            )}
          </div>

          {dismissible && onDismiss && (
            <button
              onClick={onDismiss}
              className={cn(
                'flex-shrink-0 p-1 hover:opacity-70 transition-opacity',
                getIconColor()
              )}
              aria-label="Dismiss alert"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);
Alert.displayName = 'Alert';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription };
